import React, { useState } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Clock, Send, BookOpen, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { seoMetadata, structuredData } from '../data/seo';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title={seoMetadata.contact.title}
        description={seoMetadata.contact.description}
        keywords={seoMetadata.contact.keywords}
        canonical={seoMetadata.contact.canonical}
        additionalStructuredData={structuredData.breadcrumb('Contact Us', '/contact')}
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/eb3e1544-7565-450f-a2a0-dd9eccd226a4/images/da4027423647382c8453dda8f5235de4d709d1db1682457d5f328ef9ae6e2334.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-200">
            Let's Build Your Dream Home Together
          </p>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto -mt-20 relative z-10">
            <a href={`tel:${contactInfo.phone}`}>
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-white cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-red-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
                  <p className="text-red-600 font-semibold">{contactInfo.phone}</p>
                  <p className="text-sm text-gray-600 mt-2">{contactInfo.businessHours}</p>
                </CardContent>
              </Card>
            </a>

            <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-white cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-green-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <MessageCircle className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
                  <p className="text-green-600 font-semibold">Chat with us</p>
                  <p className="text-sm text-gray-600 mt-2">Quick response guaranteed</p>
                </CardContent>
              </Card>
            </a>

            <a href={`mailto:${contactInfo.email}`}>
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 bg-white cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                  <p className="text-blue-600 font-semibold text-sm">{contactInfo.email}</p>
                  <p className="text-sm text-gray-600 mt-2">We'll reply within 24 hours</p>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Get Expert Homebuilding Guidance
                </h2>
                <p className="text-lg text-gray-600 mb-3">
                  Fill out the form and our material expert will contact you within 24 hours
                </p>
                <Link to="/ebook" className="inline-flex items-center text-red-600 hover:text-red-700 font-semibold text-sm group">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Or download Our Premium Homebuilding Guide
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="e.g., Enquiry about tiles, Price quote, etc."
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    className="w-full"
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    required
                    className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label htmlFor="consent" className="text-sm text-gray-600">
                    I agree that my submitted data is being collected
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Visit Our Showroom
                </h2>
                <p className="text-lg text-gray-600">
                  Experience our premium collection in person
                </p>
              </div>

              <div className="space-y-6 mb-8">
                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                        <p className="text-gray-600">{contactInfo.address}</p>
                        <a
                          href={contactInfo.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:text-red-700 font-medium mt-2 inline-block"
                        >
                          Get Directions →
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Business Hours</h3>
                        <div className="space-y-1 text-gray-600">
                          <p className="font-medium">Open All Days</p>
                          <p>10:00 AM - 08:30 PM</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Google Map */}
              <div className="rounded-2xl overflow-hidden shadow-xl h-80">
                <iframe
                  src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Bombay+Home+Gallery+-+Best+Floor+Tile+Shop+Near+You,Lucknow&zoom=17"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Bombay Home Gallery Location"
                />
              </div>
              
              {/* Navigate Button */}
              <div className="mt-4 text-center">
                <a
                  href="https://www.google.com/maps/dir//Bombay+Home+Gallery+-+Best+Floor+Tile+Shop+Near+You,+Hardoi+Road,+opposite+Azad+Patakhe,+Haji+Colony,+Mahipatmau,+Lucknow,+Uttar+Pradesh+226003"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Navigate to Our Showroom
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prefer to Talk Directly?
          </h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Call us now for immediate assistance and expert guidance
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${contactInfo.phone}`}>
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 shadow-xl">
                <Phone className="w-5 h-5 mr-2" />
                Call {contactInfo.phone}
              </Button>
            </a>
            <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
